import { NgClass, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SearchBarComponent } from '@app/core/header/search-bar/search-bar.component';
import { AuthService } from '@app/core/shared/auth.service';
import { SidenavMenuComponent } from '@app/core/sidenav-menu/sidenav-menu.component';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbModal,
  NgbTooltip
} from '@ng-bootstrap/ng-bootstrap';
import { User } from '@shared/graphql/generated';
import { CallbackModalStoreService } from '@shared/modals/callback-modal/shared/callback-modal-store.service';
import { CallbackType } from '@shared/modals/callback-modal/shared/callback-type';
import {
  ConfirmationModalStoreService
} from '@shared/modals/confirmation-modal/shared/confirmation-modal-store.service';
import {
  ResetPasswordModalStoreService
} from '@shared/modals/reset-password-modal/shared/reset-password-modal-store.service';
import { NavLink, NavLinkWithIcon } from '@shared/models/nav-link';
import { ToastType } from '@shared/models/toast-type';
import { AuthModalService } from '@shared/services/auth-modal.service';
import { ComparisonStoreService } from '@shared/stores/comparison-store.service';
import { ToastStoreService } from '@shared/stores/toast-store.service';
import { UserStoreService } from '@shared/stores/user-store.service';
import { AvatarPlaceholderComponent } from '@shared/ui-components/avatar-placeholder/avatar-placeholder.component';
import { filter } from 'rxjs';

interface NotificationBar {
  style: 'danger' | 'warning';
  action: 'account' | 'emailConfirmation' | 'password';
  message: string;
}

@Component({
  selector: 'evland-header',
  templateUrl: './header.component.html',
  styleUrls: [ './header.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    MatIcon,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    NgbTooltip,
    SearchBarComponent,
    AvatarPlaceholderComponent,
    SidenavMenuComponent,
  ]
})
export class HeaderComponent {
  @ViewChild('sidenavMenuContent') sidenavMenuContent: ElementRef;

  private authService = inject(AuthService);
  private authModal = inject(AuthModalService);
  private callbackModal = inject(CallbackModalStoreService);
  private confirmationModal = inject(ConfirmationModalStoreService);
  private comparisonStore = inject(ComparisonStoreService);
  private modalService = inject(NgbModal);
  private resetPasswordModal = inject(ResetPasswordModalStoreService);
  private router = inject(Router);
  private toast = inject(ToastStoreService);
  private userStore = inject(UserStoreService);

  navLinks: NavLink[] = [
    {
      label: $localize`Автомобілі`,
      children: [
        { href: '/catalog', label: $localize`Каталог` },
        { href: '/warehouse', label: $localize`В наявності` },
        { href: '/offers', label: $localize`З пробігом` },
        { href: '/reviews', label: $localize`Відгуки`, badge: 'NEW' },
      ],
    },
    {
      label: $localize`Послуги`,
      children: [
        { href: '/financing', label: $localize`Фінансування` },
        { href: '/euro-charge', label: $localize`EuroCharge™` },
        { href: '/zeekr-check', label: $localize`Перевірка ZEEKR` },
      ]
    },
    {
      label: $localize`Компанія`,
      children: [
        { href: '/about-us', label: $localize`Про нас` },
        { href: '/contact-us', label: $localize`Контакти` },
      ]
    },
  ];
  dropdownState: WritableSignal<boolean[]> = signal(this.navLinks.map(() => false));
  userMenuLinks: NavLinkWithIcon[] = [
    { icon: 'sax:twotone-user', href: '/account/personal', label: $localize`Особиста інформація` },
    { icon: 'sax:twotone-heart', href: '/account/favorite', label: $localize`Вибране` },
    { icon: 'sax:twotone-shopping-bag', href: '/account/orders', label: $localize`Заявки та замовлення` },
    { icon: 'sax:twotone-message-2', href: '/account/reviews', label: $localize`Відгуки` },
    { icon: 'sax:twotone-link', href: '/account/accounts', label: $localize`Облікові записи та зв'язки` },
  ];
  currentUser: Signal<User>;
  currentUserFullName: Signal<string>;
  modelsForComparison: Signal<number>;
  notificationBar: Signal<NotificationBar>;

  constructor() {
    this.initSignals();
  }

  onDropdownOpenChange(index: number, newState: boolean): void {
    this.dropdownState.set(
      this.dropdownState()
        .map((state: boolean, i: number) => index === i ? newState : state)
    );
  }

  onLogoutClick(): void {
    this.confirmationModal.show({
      title: $localize`Увага`,
      message: $localize`Ви впевнені, що хочете вийти з облікового запису?`,
      confirmButtonPalette: 'danger',
    })
      .closed
      .pipe(
        filter(Boolean),
      )
      .subscribe({
        next: () => this.authService.logout(),
      });
  }

  onMenuToggleClick() {
    this.modalService.open(
      this.sidenavMenuContent,
      {
        ariaLabelledBy: 'modal-basic-title',
        fullscreen: true,
        windowClass: 'sidenav-menu'
      },
    );
  }

  onNotificationBarClick(): void {
    switch (this.notificationBar().action) {
      case 'account':
        this.router.navigate([ '/account/personal' ]);
        break;
      case 'emailConfirmation':
        this.requestEmailConfirmation();
        break;
      case 'password':
        this.resetPasswordModal.show();
        break;
    }
  }

  openCallbackModal(): void {
    this.callbackModal.show({
      type: CallbackType.Consultation,
      title: $localize`Замовити консультацію`,
      action_label: $localize`Замовити`,
      subject: $localize`Консультація`,
    });
  }

  openSignInModal(): void {
    this.authModal.show('signIn');
  }

  requestEmailConfirmation(): void {
    this.authService.requestEmailConfirmation()
      .subscribe({
        next: () => this.toast.show({
          text: $localize`Лист із посиланням на підтвердження пошти надіслано. Не забудьте перевірити папку "Спам"`,
          type: ToastType.Success,
        }),
        error: () => this.toast.show({
          text: $localize`Щось пішло не так, можливо пошта вже підтверджена або ви нещодавно вже запитали листа`,
          type: ToastType.Error,
        }),
      });
  }

  private initSignals(): void {
    this.modelsForComparison = computed(() => this.comparisonStore.listS().length);

    this.currentUser = this.userStore.currentS;

    this.currentUserFullName = computed(() => {
      const currentUser = this.currentUser();

      if (!currentUser) return null;

      return [ currentUser.last_name, currentUser.first_name, currentUser.middle_name ].filter(Boolean).join(' ');
    });

    this.notificationBar = computed(() => {
      const currentUser = this.currentUser();

      if (!currentUser) return null;

      const { email, email_confirmed, email_confirmation_token_expires_at, has_password, phone_number, preferred_office } = currentUser;

      const isAccountIssue = !email || !phone_number || !preferred_office;
      const isEmailConfirmationIssue = !email_confirmed && new Date(email_confirmation_token_expires_at) < new Date();
      const isPasswordIssue = !has_password;

      if (!isAccountIssue && !isEmailConfirmationIssue && !isPasswordIssue) return null;

      return {
        style: isAccountIssue ? 'danger' : 'warning',
        action: isAccountIssue ? 'account' : isEmailConfirmationIssue ? 'emailConfirmation' : 'password',
        message: isAccountIssue
          ? $localize`Для отримання кращого досвіду, будь ласка, заповніть обов'язкові контактні дані в особистому кабінеті`
          : isEmailConfirmationIssue
            ? $localize`Ваша пошта досі не підтверджена. Бажаєте запросити повторний лист із посиланням?`
            : $localize`Ваш обліковий запис не має пароля. Будь ласка, встановіть його для зручності авторизації`
      };
    });
  }
}
